import _ from 'lodash';
import { getC25Plan, isC25Plan } from '@/common/hardcode';
import { plansDataMapper } from '@/common/service/plan-data-mapper';
import {
  getPlanTypeByCategory,
  parseSubscription2PlanList,
  shouldApplyAmountFromSubscription,
} from '@/common/service/parse';
import { getPbxSubscriptions, getPlanMap, getSubscriptions } from '@/apis';
import { getCurrencySwitchSubscriptions } from '@/apis/currency-switch';
import { logger } from '@/utils/log';
import { isDebug, isDevEnv, isEmptyVal } from '@/utils';
import { billingConfig$Cache } from './common';
import {
  BILLING_CYCLE,
  BillingCycleEnum,
  planTypes,
} from '@/common/constants';
import { flowTypes, pageConfig } from '@/common/service/page-config';
import { getDIDPlanSubscriptions } from '@/plans/TOLLFREE/helpers/flow';
import {
  getCurrentEditPlan,
  hasUnSupportCyclePlan,
  isOnlineNotSupportPlan,
  loadNonStandardBillingCyclePlanData,
} from '@/common/service/non-standard-billing-cycle-utils';
import { simulateSingleUseProPlan } from './single-use'
import { getAllProductInfo } from '@/flows/helper/get-plans-data';

/**
 * merge account subscription and pbx subscription
 * @param subscriptionsList
 * @return {null|[]}
 */
export function mergeSubscriptionList(...subscriptionsList) {
  return _.chain(subscriptionsList)
    .reduce((result, subscriptions) => {
      _.forEach(subscriptions, (item) => {
        const subscriptionItem = result[item.plan_code] || {};
        result[item.plan_code] = {
          ...subscriptionItem,
          ...item,
        };
      });
      return result;
    }, {})
    .values()
    // .filter(item => item.quantity)
    .value();
}

/**
 *
 * @param options {BuildAccountBaseServicePayload}
 * @return {Promise<null|*[]>}
 */
export const getAccountSubscriptions = async (options = {}) => {
  const { isNeedCallPbx = false } = options;

  const [subscriptions, pbxSubscriptions] = await Promise.all([
    getSubscriptions(),
    isNeedCallPbx ? getPbxSubscriptions() : Promise.resolve([]),
  ]);

  return mergeSubscriptionList(subscriptions, pbxSubscriptions);
};

/**
 * Get currency switch subscriptions - similar to getAccountSubscriptions but for currency switch
 * @param {string} currency - Target currency
 * @param {object} options - Options
 * @return {Promise<*[]>}
 */
export const getCurrencySwitchAccountSubscriptions = async (currency, options = {}) => {
  const { isNeedCallPbx = false } = options;

  const [subscriptions, pbxSubscriptions] = await Promise.all([
    getCurrencySwitchSubscriptions(currency),
    isNeedCallPbx ? getPbxSubscriptions() : Promise.resolve([]),
  ]);

  const currencySwitchData = subscriptions.result || subscriptions;

  return mergeSubscriptionList(currencySwitchData, pbxSubscriptions);
};

export const generateAccountBoughtPlans = (subscriptions = [], planList = [], billingConfig) => {
  return subscriptions
    .map((subscription) => parseSubscription2PlanList(planList, subscription, billingConfig));
};

export const getAccountFreeTrialPlans = (subscriptions = [], planList = [], billingConfig) => {
  return subscriptions
    .filter(subscription => subscription['isFreeTrial'] || subscription['isOnlineFreeTrial'])
    .map((subscription) => parseSubscription2PlanList(planList, subscription, billingConfig));
};

const replaceAmountForCoupon = (oldPlanInfo, planInfo, subscriptionInfo, {
  isConsideredScenes = false,
}) => {
  const flowType = pageConfig?.type;
  const { oldPlanCode } = pageConfig?.editConfig;
  const isFromCampaignLink = !isEmptyVal(pageConfig?.marketingCoupon);
  const discountInfo = _.get(pageConfig?.marketingCoupon, "discountInfo", {});

  // For some cases with marketing coupon (replace subscription), need use planmap.json price
  // special case for marketing coupon (edit flow [a2a, a2m, m2m, m2a])
  if (isConsideredScenes
    && _.includes([flowTypes.edit], flowType)
    && isFromCampaignLink
    && oldPlanCode === oldPlanInfo.plan_code
    && _.has(discountInfo, oldPlanCode)) {
    return ({
      ...planInfo,
      amount: oldPlanInfo.amount,
      onlineAmount: oldPlanInfo.amount,
      isStandardPrice: true,
    });
  }

  // For regular edit flow with discount
  if (subscriptionInfo.discount) {
    // useStdPrice -> means need to use standard prices?
    // BIZ INCREASE
    const planAmount = subscriptionInfo?.useStdPrice ? subscriptionInfo?.stdPrice : planInfo?.amount;

    return ({
      ...planInfo,
      amount: planAmount || planInfo?.amount,
      onlineAmount: planInfo.amount,
      isStandardPrice: true,
    });
  }

}

function createLoaderLog (planList, subscriptions, boughtPlans){
  logger.groupCollapsed('loader paid account base information: ');
  logger.log(`planList:`, planList);
  logger.log(`boughtPlans:`, boughtPlans);
  logger.log(`subscriptions:`, subscriptions);
  logger.groupEnd();
}

const resetPlanAmountByUserSubscription = (planList, subscriptions, billingConfig) => {
  return planList.map(item => {

    // TODO: Add comments.
    let planInfo = {...item};
    // PRO INCREASE - There are new and old plan types for Pro annually & monthly, need to check if apply new price plan type for Pro annually & monthly
    // Major judge pro monthly/annual plan is customer price
    const needReplacePlan = subscriptions.find(subs => {
      return subs.plan_code !== item.plan_code && shouldApplyAmountFromSubscription(subs.plan_code, item.plan_code, billingConfig);
    });

    // if the needReplacePlan is not empty, means the pro monthly/annual plan is custom price
    if(needReplacePlan) {
      planInfo.plan_code = needReplacePlan.plan_code;
      planInfo.amount = needReplacePlan.price;
    }

    const subscriptionInfo = subscriptions.find(subs => {
      if (isOnlineNotSupportPlan({ planType: getPlanTypeByCategory(subs.category), billingCycle: subs.billingCycle, category: subs.category })) {
        return subs.plan_code === planInfo.plan_code && BillingCycleEnum[planInfo.billing_cycle] === subs.billingCycle
      }
      return subs.plan_code === planInfo.plan_code
    });
    if(!subscriptionInfo) {
      return ({
        ...planInfo,
        onlineAmount: planInfo.amount,
        isStandardPrice: true,
      });
    }
    // FREE TRIAL - free trial sub info shared the same plan code with paid plan and amount = 0, don't use sub info, use plan info directly
    if((!!subscriptionInfo?.isFreeTrial || !!subscriptionInfo?.isOnlineFreeTrial) && subscriptionInfo?.price === 0) {
      return ({
        ...planInfo,
        freeTrialPlanInfo: subscriptionInfo,
        onlineAmount: planInfo.amount,
        isStandardPrice: true,
      });
    }

    // Replace plan amount with coupon [campaign link -> edit base/addon, regular edit flow with origin discount]
    const planInfoWithCoupon = replaceAmountForCoupon(item, planInfo, subscriptionInfo, {
      isConsideredScenes: !needReplacePlan
    });

    if (planInfoWithCoupon) {
      return planInfoWithCoupon;
    }
    /**
     * planInfo.isAmountOverwrite means use planInfo.amount to overwrite subscriptionInfo.price
     * So we doesn't assign subscriptionInfo.price to amount here
     * For this task: ZOOM-876523
     */
    if (subscriptionInfo?.price !== planInfo.amount && planInfo.isAmountOverwrite && subscriptionInfo?.isStandardPrice) {
      return ({
        ...planInfo,
        onlineAmount: planInfo.amount,
        isStandardPrice: subscriptionInfo?.isStandardPrice
      })
    }

    const isStandardPrice = subscriptionInfo?.isStandardPrice ?? subscriptionInfo.price === planInfo.amount;
    return ({
      ...planInfo,
      amount: subscriptionInfo.price,
      onlineAmount: planInfo.amount,
      isStandardPrice,
    })
  })
}

const resetPlanListWithNonStandardPrice = async (planList = [], subscriptions = [], isShowC25Plan = false) => {
  if (isShowC25Plan) {
    const { result } = await getPlanMap(true);
    const newPricingList = plansDataMapper(result);
    const C25Plan = getC25Plan(subscriptions);
    const C25PlanBillingCycle = C25Plan.billingCycle === BillingCycleEnum[BILLING_CYCLE.MONTHLY] ? BILLING_CYCLE.MONTHLY : BILLING_CYCLE.ANNUAL;

    const legacyC25Plan = _.find(planList, item => item.category === C25Plan.category && item.billing_cycle === C25PlanBillingCycle);
    const legacyC25PlanIndex = _.findIndex(newPricingList, item => item.category === C25Plan.category && item.billing_cycle === C25PlanBillingCycle);

    legacyC25Plan && (newPricingList[legacyC25PlanIndex] = legacyC25Plan);

    return newPricingList;
  }

  return planList;
}

function hasUnlimitedAudio(subscriptions) {
  return !!subscriptions.find(item => item.category === 'tollfree_us_ca');
}

/**
 * * Just for paid account
 * generate account bought plans & subscriptions & planList
 * @param options {BuildAccountBaseServicePayload}
 * @return {Promise<{subscriptions: (null|*[]), boughtPlans: *[], planMap: []}>}
 */
export const buildAccountBaseService = async (options = {}) => {
  const { isNeedCheckC25Plan = false, currentEditPlanCode, isCheckFromCampaign = false } = options;
  const oldUnlimitedPlanCode = pageConfig?.oldUnlimitedPlanCode
  // get account subscription list
  const subscriptions = await getAccountSubscriptions(options);
  const isShowC25Plan = isNeedCheckC25Plan && isC25Plan(subscriptions);
  const isC50Plan = !isShowC25Plan;
  const hasUnsupportBillingCyclePlan = hasUnSupportCyclePlan(subscriptions, currentEditPlanCode || oldUnlimitedPlanCode);
  isDebug && console.log('smb:hasUnsupportBillingCyclePlan:', hasUnsupportBillingCyclePlan);
  const hasBoughtUnlimitedAudio = hasUnlimitedAudio(subscriptions);

  const getAllProductInfoPromise = (...args) => getAllProductInfo(...args).then(({ planMap }) => planMap);
  // get plan map info
  const [configRes, planMapRes] = await Promise.all([
    billingConfig$Cache(),
    getAllProductInfoPromise(isC50Plan, hasUnsupportBillingCyclePlan, hasBoughtUnlimitedAudio, isCheckFromCampaign)
  ]);

  simulateSingleUseProPlan(planMapRes)

  // for c25 plan format
  const billingConfig = configRes.result;
  if (hasUnsupportBillingCyclePlan) {
    let currentPlanCode = currentEditPlanCode;
    if (!currentEditPlanCode && pageConfig?.from_sub === planTypes.ZOOM_ONE_BUNDLE) {
      currentPlanCode = subscriptions.find((s) => getPlanTypeByCategory(s.category) === planTypes.ZOOM_ONE_BUNDLE)?.plan_code;
    } else if (!currentEditPlanCode && pageConfig?.from_sub === planTypes.ZOOM_PHONE) {
      currentPlanCode = subscriptions.find((s) => getPlanTypeByCategory(s.category) === planTypes.ZOOM_PHONE)?.plan_code;
    } else if (!currentEditPlanCode && oldUnlimitedPlanCode) {
      currentPlanCode = oldUnlimitedPlanCode;
    } else if (!currentPlanCode && pageConfig?.type === flowTypes.upsell) {
      const planType = pageConfig?.editConfig?.planType;
      currentPlanCode = subscriptions.find((s) => getPlanTypeByCategory(s.category) === planType)?.plan_code;
    }
    const currentEditPlan = getCurrentEditPlan(pageConfig?.type, subscriptions, currentPlanCode);
    await loadNonStandardBillingCyclePlanData(planMapRes, currentEditPlan, pageConfig?.type, subscriptions);
  }

  // generate plan list with pre plan structure
  let planList = plansDataMapper(planMapRes, subscriptions);

  const DIDPlanSubscriptions = getDIDPlanSubscriptions(subscriptions, planList);
  subscriptions.push(...DIDPlanSubscriptions);

  planList = await resetPlanListWithNonStandardPrice(planList, subscriptions, isShowC25Plan);
  if(!billingConfig?.isExpireAccountWithPaidPlan){
  planList = resetPlanAmountByUserSubscription(planList, subscriptions, billingConfig);
  }

  // generate bought plan list with pre plan structure
  const boughtPlans = generateAccountBoughtPlans(subscriptions, planList, billingConfig);
  // generate free trial plan list with pre plan structure
  const freeTrialPlans = getAccountFreeTrialPlans(subscriptions, planList, billingConfig);

  isDevEnv && createLoaderLog(planList, subscriptions, boughtPlans);

  return {
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    allPlanMap: planMapRes,
    planMap: planList,
  };
};
